import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/DeadlinesGenerateModal.translations.json';
import { useMemo } from 'react';
import { Alert, Flex } from 'antd';
import {
  Form,
  FormDateRangePicker,
  FormField,
  FormInputNumber,
  FormRadioGroup,
} from '../../common/form';
import {
  dayType,
  DeadlineGenerationFormSchema,
} from '../schema/DeadlineGenerationFormSchema';
import {
  ActionButton,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
} from '../../common/components';
import { Modal } from '../../common/components/modal/Modal';
import { FormTimePicker } from '../../common/form/input/FormTimePicker';
import { DeadlineGenerationFormType } from '../schema';
import * as Icon from 'react-feather';

const { addTranslations, t } = i18nNsFactory('deadline-generate-modal');
addTranslations(translations);

export interface DeadlinesGenerateModalProps {
  onCancel: () => void;
  onGenerate: (formData: DeadlineGenerationFormType) => void;
  generateError?: string;
  isGenerating?: boolean;
}

const defaultValues: Partial<DeadlineGenerationFormType> = {
  dayType: 'BUSINESS_DAY',
  time: '23:59',
};

export function DeadlinesGenerateModal({
  onCancel,
  onGenerate,
  generateError,
  isGenerating,
}: DeadlinesGenerateModalProps) {
  const dayTypeOptions = useMemo(() => {
    return dayType.map((type) => ({
      value: type,
      label: t(`fields.dayType.options.${type}`),
    }));
  }, []);

  return (
    <Modal onCancel={onCancel} width="50rem" footer={null}>
      <ModalHeader>
        <ModalTitle>{t('title')}</ModalTitle>
        {generateError && (
          <Padding top={8}>
            <Alert
              type="error"
              icon={<Icon.AlertTriangle />}
              showIcon
              message={generateError}
              banner
            />
          </Padding>
        )}
      </ModalHeader>
      <Form
        onSubmit={onGenerate}
        schema={DeadlineGenerationFormSchema}
        defaultValues={defaultValues}
      >
        <Padding scrollable>
          <Flex vertical gap="large">
            <FormField
              name="dateRange"
              label={t('fields.dateRange.label')}
              required
            >
              <FormDateRangePicker
                name="dateRange"
                style={{ width: '25rem' }}
              />
            </FormField>
            <FormField
              name="daysAfter"
              label={t('fields.deadline.label')}
              required
            >
              <Flex gap="small" align="center">
                <FormInputNumber
                  name="daysAfter"
                  style={{ maxWidth: '5rem' }}
                  min={1}
                  precision={0}
                />
                <FormRadioGroup
                  name="dayType"
                  options={dayTypeOptions}
                  type="button"
                />
                kell
                <FormTimePicker
                  name="time"
                  style={{ width: '10rem' }}
                  timeFormat="HH:mm"
                  allowClear={false}
                />
              </Flex>
            </FormField>
          </Flex>
        </Padding>
        <ModalFooter>
          <Flex justify="end" gap="small">
            <ActionButton
              type="abort"
              onClick={onCancel}
              disabled={isGenerating}
            />
            <ActionButton
              label={t('buttons.submit')}
              type="formSubmit"
              loading={isGenerating}
            />
          </Flex>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
